// ABOUTME: The session's one fixed answer to "has this browser seen the opening film recently?",
// ABOUTME: plus the entry-time touch that restarts the 7-day returning-guest window (SPEC-43, R1).

import { useEffect, useRef, useState } from 'react';
import { hasSeenIntro, touchIntroSeen } from './visitMemory';

/**
 * Reads the visit record ONCE, at mount, and re-stamps it the first time the guest actually enters
 * the boutique this page load.
 *
 * Why read once: the value feeds entry gating, the Projectionist's boot chapter and the
 * `returning` flag the backend words its welcome from. They must all agree for the whole session,
 * and `markIntroSeen` runs at the film's end — after every one of those decisions — so a live read
 * would flip under them. The lazy initialiser is the same idiom App.js used before this hook.
 *
 * Why touch on entry: a returning guest's boot chapter is a silent Freeze hold that never reaches
 * `handleChapterEnd`, so `markIntroSeen` is never re-run on a returning visit. Without an explicit
 * touch the window would count from the first viewing rather than from the last visit, and a guest
 * who came back daily would still be shown the film again on day eight. `touchIntroSeen` refreshes
 * only a record that is still valid — it never creates one and never resurrects an expired one —
 * so a first-time guest (or one whose window lapsed) is unaffected by this hook until the film's
 * end writes a fresh record.
 *
 * Manual test (SPEC-43 R1): in DevTools set `localStorage.versai_seen_intro` to
 * `{"v":1,"ts":<Date.now() - 6 days>}`, reload, press Enter Boutique — the film is skipped and the
 * stored `ts` is now within a second of the present. Set `ts` to `Date.now() - 8 days` instead,
 * reload and enter — the film plays, the stored value is NOT rewritten until the film ends.
 *
 * @param {object}  p
 * @param {boolean} p.entered  true once the guest has pressed Enter Boutique (`isExperienceStarted`)
 * @returns {{ hasSeenIntroBefore: boolean }}
 */
export default function useVisitMemory({ entered = false } = {}) {
  const [hasSeenIntroBefore] = useState(() => hasSeenIntro()); // read ONCE, stable for the session
  const touchedRef = useRef(false);

  useEffect(() => {
    if (!entered || !hasSeenIntroBefore || touchedRef.current) return;
    touchedRef.current = true;
    touchIntroSeen(); // "visit" = the guest entered the boutique this page load
  }, [entered, hasSeenIntroBefore]);

  return { hasSeenIntroBefore };
}

export { useVisitMemory };
